import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useNotif } from '@/context/NotificationContext';
import { useI18n } from '@/i18n';
import { fileUrl } from '@/lib/api';
import AuthModal from '@/components/AuthModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { LogOut, User as UserIcon, Map as MapIcon, Users, MessageCircle, ImagePlus, Bell, UserPlus, Heart, MessageSquare, CheckCheck, Trash2 } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const notif = useNotif();
  const dmBadge = notif?.totalUnread || 0;
  const notifBadge = (notif?.pendingInCount || 0) + (notif?.convoyRequestsCount || 0) + (notif?.activityUnread || 0);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-home-link">
          <div className="font-display font-black uppercase tracking-tighter text-xl">
            Horizon<span className="text-[#FF007F]">.</span>Meet<span className="text-white/50">&</span><span className="text-[#00F0FF]">Cruise</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm text-white/70">
          <Link to="/" className="hover:text-white transition" data-testid="nav-link-home">{t('nav.home')}</Link>
          <Link to="/maps/fh6" className="hover:text-white transition" data-testid="nav-link-map">{t('nav.map')}</Link>
          <Link to="/convoys" className="hover:text-white transition inline-flex items-center gap-1" data-testid="nav-link-convoys">
            <Users className="w-3.5 h-3.5" /> {t('nav.convoys')}
          </Link>
          <Link to="/media" className="hover:text-white transition inline-flex items-center gap-1" data-testid="nav-link-media">
            <ImagePlus className="w-3.5 h-3.5" /> {t('nav.media')}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user && user !== false && (
            <DropdownMenu
              onOpenChange={(open) => {
                if (open && (notif?.activityUnread || 0) > 0) {
                  // Soft auto-mark-read after a small delay so the user sees the highlighted state briefly
                  setTimeout(() => notif?.markAllRead?.(), 1500);
                }
              }}
            >
              <DropdownMenuTrigger asChild data-testid="nav-notif-menu">
                <Button
                  variant="ghost"
                  data-testid="nav-notif-btn"
                  className="text-white hover:bg-white/10 rounded-full w-10 h-10 p-0 relative"
                  title={t('nav.notifications') || 'Notifications'}
                >
                  <Bell className={`w-4 h-4 ${notifBadge > 0 ? 'text-[#FFD479]' : ''}`} />
                  {notifBadge > 0 && (
                    <span
                      data-testid="nav-notif-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFD479] text-black text-[10px] font-bold grid place-items-center leading-none ring-2 ring-[#0A0A0A]"
                    >
                      {notifBadge > 9 ? '9+' : notifBadge}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="glass-strong text-white border-white/10 min-w-[340px] max-h-[500px] overflow-y-auto"
                data-testid="nav-notif-dropdown"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="text-white/70 inline-flex items-center gap-2 p-0">
                    <Bell className="w-3.5 h-3.5" /> {t('nav.notifications') || 'Notifications'}
                  </DropdownMenuLabel>
                  <div className="flex items-center gap-1">
                    {(notif?.activityUnread || 0) > 0 && (
                      <button
                        type="button"
                        data-testid="nav-notif-mark-all-read"
                        onClick={(e) => { e.preventDefault(); notif.markAllRead?.(); }}
                        title={t('notif.mark_all_read') || 'Tout marquer comme lu'}
                        className="text-[10px] text-white/50 hover:text-white inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition"
                      >
                        <CheckCheck className="w-3 h-3" /> {t('notif.mark_all_read_short') || 'Tout lu'}
                      </button>
                    )}
                    {(notif?.items?.length || 0) > 0 && (
                      <button
                        type="button"
                        data-testid="nav-notif-clear-all"
                        onClick={(e) => { e.preventDefault(); notif.clearAll?.(); }}
                        title={t('notif.clear_all') || 'Tout effacer'}
                        className="text-[10px] text-white/40 hover:text-red-300 inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />

                {notifBadge === 0 && (notif?.items?.length || 0) === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-white/40" data-testid="nav-notif-empty">
                    {t('notif.empty') || 'Aucune notification'}
                  </div>
                )}

                {/* Convoy join requests */}
                {(notif?.convoyRequests || []).map((cv) => (
                  <DropdownMenuItem
                    key={`cv-${cv.convoy_id}`}
                    data-testid={`nav-notif-convoy-${cv.convoy_id}`}
                    className="focus:bg-white/10 cursor-pointer flex items-start gap-2 py-2.5"
                    onClick={() => navigate(`/convoys/${cv.convoy_id}`)}
                  >
                    <Users className="w-4 h-4 mt-0.5 text-[#FFD479] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold leading-tight">
                        {cv.requests?.length === 1
                          ? `${cv.requests[0].name} ${t('notif.convoy_request') || 'veut rejoindre'}`
                          : `${cv.requests?.length} ${t('notif.convoy_requests_plural') || 'demandes pour'}`} <span className="text-[#FFD479]">{cv.convoy_name}</span>
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5">
                        {t('notif.click_to_manage') || 'Cliquer pour gérer'}
                      </div>
                    </div>
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FFD479] text-black text-[9px] font-black shrink-0">
                      {cv.requests?.length || 0}
                    </span>
                  </DropdownMenuItem>
                ))}

                {/* Friend requests */}
                {(notif?.friends?.pending_in || []).length > 0 && notif?.convoyRequests?.length > 0 && (
                  <DropdownMenuSeparator className="bg-white/10" />
                )}
                {(notif?.friends?.pending_in || []).map((f) => (
                  <DropdownMenuItem
                    key={`fr-${f.id}`}
                    data-testid={`nav-notif-friend-${f.id}`}
                    className="focus:bg-white/10 cursor-pointer flex items-start gap-2 py-2.5"
                    onClick={() => navigate(`/u/${f.id}`)}
                  >
                    <UserPlus className="w-4 h-4 mt-0.5 text-[#FF007F] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold leading-tight truncate">
                        <span className="text-[#FF007F]">{f.name}</span> {t('notif.friend_request') || "t'as envoyé une demande d'ami"}
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5">
                        {t('notif.click_to_view') || 'Cliquer pour voir le profil'}
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}

                {/* Activity feed (likes/comments/reactions/follows) */}
                {(notif?.items?.length || 0) > 0 && (notif?.convoyRequests?.length > 0 || (notif?.friends?.pending_in || []).length > 0) && (
                  <DropdownMenuSeparator className="bg-white/10" />
                )}
                {(notif?.items || []).map((n) => {
                  const ActivityIcon = ['spot_like', 'media_like'].includes(n.type)
                    ? Heart
                    : ['spot_comment', 'media_comment'].includes(n.type)
                      ? MessageSquare
                      : n.type === 'follow' ? UserPlus : Bell;
                  const iconColor =
                    n.type === 'follow'
                      ? 'text-[#A78BFA]'
                      : ['spot_comment', 'media_comment'].includes(n.type)
                        ? 'text-[#00F0FF]'
                        : 'text-[#FF007F]';
                  const target =
                    n.target_type === 'media' ? '/media' :
                    n.target_type === 'spot' ? '/maps/fh6' :
                    n.target_type === 'user' ? `/u/${n.actor_id}` : '/';
                  const msg = (() => {
                    switch (n.type) {
                      case 'spot_like':
                        return <>{n.actor_name} <span className="text-white/60">a aimé ton spot</span> "<span className="text-white">{n.target_title}</span>"</>;
                      case 'spot_comment':
                        return <>{n.actor_name} <span className="text-white/60">a commenté ton spot</span> "<span className="text-white">{n.target_title}</span>"</>;
                      case 'media_like':
                        return <>{n.actor_name} <span className="text-white/60">a aimé ta publication</span> "<span className="text-white">{n.target_title}</span>"</>;
                      case 'media_comment':
                        return <>{n.actor_name} <span className="text-white/60">a commenté</span> "<span className="text-white">{n.target_title}</span>"</>;
                      case 'media_reaction':
                        return <>{n.actor_name} <span className="text-white/60">a réagi</span> {n.extra?.emoji} <span className="text-white/60">à</span> "<span className="text-white">{n.target_title}</span>"</>;
                      case 'follow':
                        return <>{n.actor_name} <span className="text-white/60">te suit maintenant</span></>;
                      default:
                        return n.actor_name;
                    }
                  })();
                  return (
                    <DropdownMenuItem
                      key={`act-${n.id}`}
                      data-testid={`nav-notif-activity-${n.id}`}
                      className={`focus:bg-white/10 cursor-pointer flex items-start gap-2 py-2.5 ${!n.read ? 'bg-white/[0.03]' : ''}`}
                      onClick={() => navigate(target)}
                    >
                      {!n.read && (
                        <span className="absolute left-0.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#FF007F]" />
                      )}
                      <ActivityIcon className={`w-4 h-4 mt-0.5 ${iconColor} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs leading-tight">{msg}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`nav-notif-activity-delete-${n.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); notif.deleteItem?.(n.id); }}
                        className="text-white/30 hover:text-red-300 transition shrink-0"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild data-testid="nav-user-menu">
                <Button
                  data-testid="nav-user-btn"
                  variant="ghost"
                  className="text-white hover:bg-white/10 rounded-full px-3 gap-2 relative"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name} className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#00F0FF] text-black grid place-items-center text-xs font-bold">
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline">{user.name || user.email}</span>
                  {dmBadge > 0 && (
                    <span data-testid="nav-messages-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF007F] text-white text-[10px] font-bold grid place-items-center leading-none ring-2 ring-[#0A0A0A]">
                      {dmBadge > 9 ? '9+' : dmBadge}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass-strong text-white border-white/10 min-w-[200px]">
                <DropdownMenuLabel className="text-white/70">{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="nav-profile-link"
                  className="focus:bg-white/10 cursor-pointer"
                  onClick={() => navigate('/profile')}
                >
                  <UserIcon className="w-4 h-4 mr-2" /> {t('nav.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-link-messages"
                  className="focus:bg-white/10 cursor-pointer"
                  onClick={() => navigate('/messages')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> {t('nav.messages')}
                  {dmBadge > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF007F] text-white text-[10px] font-bold grid place-items-center leading-none">
                      {dmBadge > 9 ? '9+' : dmBadge}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem className="focus:bg-white/10 cursor-pointer" onClick={() => navigate('/')}>
                  <MapIcon className="w-4 h-4 mr-2" /> {t('nav.home')}
                </DropdownMenuItem>
                {user.role === 'admin' && (
                  <DropdownMenuItem
                    data-testid="nav-admin-link"
                    className="focus:bg-white/10 cursor-pointer text-[#FF007F]"
                    onClick={() => navigate('/admin')}
                  >
                    <UserIcon className="w-4 h-4 mr-2" /> {t('nav.admin')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="nav-logout-btn"
                  className="focus:bg-white/10 cursor-pointer text-red-300"
                  onClick={async () => { await logout(); navigate('/'); }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : user === false ? (
            <Button
              data-testid="nav-login-btn"
              onClick={() => setAuthOpen(true)}
              className="bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
            >
              <UserIcon className="w-4 h-4 mr-2" />{t('nav.login')}
            </Button>
          ) : null}
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
